import { useState, useMemo, useRef, useEffect, useCallback, lazy, Suspense } from 'react'
import { STATUS_CONFIG, CREATOR_COLORS, AGENT_COLORS, ALL_STATUSES, MONTHS_ORDER, DATE_ACTION_LABELS, creatorInitials } from '../lib/constants'
import { useDialog } from '../lib/useDialog'
import { recalcCommissionsOn } from '../lib/commissions'
import { createDeal, updateDeal, parseContract } from '../lib/supabase'
import { canParseContract } from '../lib/permissions'
import { getDealAgents } from '../lib/team'
import { linkFieldError } from '../lib/dataHealth'

// Loaded on demand: most deal edits never touch First Look.

// --- STATUS BADGE -------------------------------------------------------------
export function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG['Concept']
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600,
      background: cfg.bg, color: cfg.color, whiteSpace: 'nowrap',
      border: `1px solid ${cfg.color}30`
    }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: cfg.dot, flexShrink: 0, boxShadow: `0 0 4px ${cfg.dot}60` }} />
      {status || 'Unknown'}
    </span>
  )
}

// --- AVATAR -------------------------------------------------------------------
export function Avatar({ name, size = 28 }) {
  const color = CREATOR_COLORS[name] || AGENT_COLORS[name] || '#6b7280'
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: color + '20', border: `1.5px solid ${color}`,
      color, fontWeight: 700, fontSize: size * 0.38,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'var(--font-display)'
    }}>
      {creatorInitials(name)}
    </div>
  )
}

// --- KPI CARD -----------------------------------------------------------------
export function KPICard({ label, value, sub, accent, icon, onClick }) {
  const [hov, setHov] = useState(false)
  return (
    <div
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      onClick={onClick}
      style={{
        background: 'var(--gradient-card)', border: '1px solid var(--card-border)',
        borderRadius: 'var(--radius-lg)', padding: '20px 20px',
        borderTop: `2px solid ${accent}`, position: 'relative', overflow: 'hidden',
        boxShadow: hov ? 'var(--shadow-md)' : 'var(--shadow-xs)',
        transform: hov ? 'translateY(-2px)' : 'translateY(0)',
        transition: 'all 0.25s var(--ease)', cursor: onClick ? 'pointer' : 'default'
      }}
    >
      <div style={{ position: 'absolute', inset: 0, boxShadow: 'var(--shadow-inner)', borderRadius: 'var(--radius-lg)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', top: 14, right: 16, fontSize: 20, opacity: hov ? 0.25 : 0.12, color: accent, transition: 'opacity 0.25s var(--ease)' }}>{icon}</div>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, fontFamily: 'var(--font-display)', color: hov ? accent : 'var(--text)', letterSpacing: '-0.5px', lineHeight: 1, transition: 'color 0.25s var(--ease)' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>{sub}</div>}
    </div>
  )
}

// --- DEAL MODAL ---------------------------------------------------------------
// --- AUTOCOMPLETE INPUT -------------------------------------------------------
function AutocompleteInput({ value, onChange, suggestions, style, ...rest }) {
  const [open, setOpen] = useState(false)
  const [activeIdx, setActiveIdx] = useState(-1)
  const wrapRef = useRef(null)
  const listRef = useRef(null)

  const filtered = useMemo(() => {
    if (!value || value.length < 1) return []
    const lower = value.toLowerCase()
    return suggestions.filter(s => s.toLowerCase().startsWith(lower) && s.toLowerCase() !== lower)
  }, [value, suggestions])

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Scroll active item into view
  useEffect(() => {
    if (activeIdx >= 0 && listRef.current) {
      const item = listRef.current.children[activeIdx]
      if (item) item.scrollIntoView({ block: 'nearest' })
    }
  }, [activeIdx])

  const handleKeyDown = useCallback((e) => {
    if (!open || filtered.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIdx(i => (i + 1) % filtered.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIdx(i => (i - 1 + filtered.length) % filtered.length)
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault()
      onChange(filtered[activeIdx])
      setOpen(false)
      setActiveIdx(-1)
    } else if (e.key === 'Escape') {
      setOpen(false)
      setActiveIdx(-1)
    }
  }, [open, filtered, activeIdx, onChange])

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        {...rest}
        value={value}
        onChange={e => { onChange(e.target.value); setOpen(true); setActiveIdx(-1) }}
        onFocus={() => { if (filtered.length) setOpen(true) }}
        onKeyDown={handleKeyDown}
        style={style}
        autoComplete="off"
      />
      {open && filtered.length > 0 && (
        <div ref={listRef} style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8,
          marginTop: 4, maxHeight: 160, overflowY: 'auto', boxShadow: 'var(--shadow-xl)'
        }}>
          {filtered.map((s, i) => (
            <div
              key={s}
              onMouseDown={(e) => { e.preventDefault(); onChange(s); setOpen(false); setActiveIdx(-1) }}
              onMouseEnter={() => setActiveIdx(i)}
              style={{
                padding: '8px 12px', fontSize: 13, cursor: 'pointer',
                background: i === activeIdx ? 'var(--accent-muted)' : 'transparent',
                color: i === activeIdx ? 'var(--accent)' : 'var(--text)',
                borderBottom: i < filtered.length - 1 ? '1px solid var(--border)' : 'none'
              }}
            >
              <span style={{ fontWeight: 600 }}>{s.slice(0, value.length)}</span>{s.slice(value.length)}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function DealModal({ deal, currentUser, onClose, onSaved, isMobile, deals, prefill, team }) {
  const isNew = !deal
  const isAdmin = currentUser?.role === 'admin'

  // Agent options come from the team, not a hardcoded list — otherwise a new
  // agent can never be set on a deal, and their commission silently never
  // computes. Keeps whatever the deal already has, so an agent who has since
  // left doesn't get blanked just by opening the record.
  const agentOptions = useMemo(() => {
    const names = getDealAgents(team)
    return [...new Set([...names, deal?.agent].filter(Boolean))].sort()
  }, [team, deal])

  const defaults = {
    date: new Date().toISOString().slice(0, 10),
    creator: '', client: '', brand: '', deliverables: '',
    deal_amount: '', agent: currentUser?.agent_name || 'Steve',
    status: 'Concept', date_action: '', month: MONTHS_ORDER[new Date().getMonth()],
    contact_person: '', email: '', notes: '',
    contract_url: '', invoice_url: '', content_url: '', beacons_url: '', media_kit_url: '',
    commission_total: 0, commission_steve: 0, commission_mirelle: 0, commission_lydie: 0, commission_loizza: 0
  }
  const [form, setForm] = useState(deal ? { ...deal } : prefill ? { ...defaults, ...prefill } : defaults)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  // Contract auto-fill state
  const [parsing, setParsing] = useState(false)
  const [parseError, setParseError] = useState(null)
  const [parsedWarnings, setParsedWarnings] = useState(null) // null = not parsed yet, [] = clean parse

  // Build autocomplete suggestions from existing deals
  const suggestions = useMemo(() => {
    if (!deals || !deals.length) return {}
    const unique = (field) => [...new Set(deals.map(d => d[field]).filter(Boolean))].sort()
    return {
      creator: unique('creator'),
      client: unique('client'),
      brand: unique('brand'),
      contact_person: unique('contact_person'),
      email: unique('email')
    }
  }, [deals])
  const acFields = new Set(['creator', 'client', 'brand', 'contact_person', 'email'])

  // Commission math lives in lib/commissions.js. Don't inline it here.
  const recalcCommissions = (u, amt, ag) => recalcCommissionsOn(u, amt, ag)

  // Reads a contract with Claude and fills the form. Only writes fields the
  // contract actually produced — a blank extraction never wipes existing input.
  const handleContractUpload = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = '' // let the same file be re-picked after an error
    if (!file) return

    setParsing(true); setParseError(null); setParsedWarnings(null)
    const { fields, error: err } = await parseContract(file)
    setParsing(false)

    if (err) { setParseError(err); return }
    if (!fields) { setParseError('Nothing could be read from that file.'); return }

    const u = { ...form }
    for (const k of ['creator', 'brand', 'client', 'contact_person', 'email', 'deliverables', 'date', 'date_action', 'notes']) {
      const v = fields[k]
      if (typeof v === 'string' && v.trim()) u[k] = v.trim()
    }
    const amt = Number(fields.deal_amount)
    if (Number.isFinite(amt) && amt > 0) u.deal_amount = amt

    // Keep month in step with the extracted date so reports bucket it correctly.
    if (u.date) {
      const m = new Date(`${u.date}T00:00:00`).getMonth()
      if (!Number.isNaN(m)) u.month = MONTHS_ORDER[m]
    }

    recalcCommissions(u, parseFloat(u.deal_amount) || 0, (u.agent || '').toLowerCase())
    setForm(u)

    const warnings = Array.isArray(fields.warnings) ? fields.warnings.filter(Boolean) : []
    // A foreign currency changes what the number means — always call it out.
    if (fields.currency && String(fields.currency).toUpperCase() !== 'CAD') {
      warnings.unshift(`Contract is in ${fields.currency} — the amount was not converted to CAD.`)
    }
    setParsedWarnings(warnings)
  }

  const handleChange = (k, v) => {
    const u = { ...form, [k]: v }
    if (k === 'deal_amount') {
      recalcCommissions(u, parseFloat(v) || 0, (u.agent || '').toLowerCase())
    }
    if (k === 'agent') {
      recalcCommissions(u, parseFloat(u.deal_amount) || 0, v.toLowerCase())
    }
    if (k === 'status' && !DATE_ACTION_LABELS[v]) {
      u.date_action = ''
    }
    setForm(u)
  }

  const dealDialog = useDialog(onClose, isNew ? 'New deal' : 'Edit deal')

  const handleSave = async () => {
    setSaving(true); setError('')
    // Link fields hold links. Checked only when the field was changed, so an
    // older record with a bad link can still be edited for something else.
    for (const [field, label] of [['contract_url', 'Contract Link'], ['invoice_url', 'Invoice Link'], ['content_url', 'Content Link'], ['beacons_url', 'Beacons Link'], ['media_kit_url', 'Media Kit Link']]) {
      const value = form[field]
      if (!isNew && (value || '') === (deal?.[field] || '')) continue
      const problem = linkFieldError(value, field, label)
      if (problem) { setError(problem); setSaving(false); return }
    }
    try {
      const payload = { ...form, deal_amount: parseFloat(form.deal_amount) || 0, date_action: form.date_action || null }
      // Steve's own deals must never save agent-less — they'd fall out of the
      // per-agent report groupings and leave the commission unallocated.
      if (!String(payload.agent || '').trim() && String(payload.creator || '').trim().toLowerCase() === 'steve') {
        payload.agent = 'Steve'
        recalcCommissionsOn(payload, payload.deal_amount, 'steve')
      }
      delete payload.id; delete payload.created_at; delete payload.updated_at
      // Double Shots referral cut. Always send the column, zero included.
      // Omitting the key on an update is not "no change" — Postgres keeps the
      // previously stored percentage, so clearing a referral back to 0 left the
      // old cut in place and kept paying it out. The column has existed since
      // the double_shots_pct migration, so there is nothing to guard against.
      payload.double_shots_pct = parseFloat(form.double_shots_pct) || 0

      // Partial collections. Only meaningful at "Partially Paid"; cleared on any
      // other status so a stale figure cannot sit on a record it no longer
      // describes. Sent explicitly (as null) for the same reason the Double
      // Shots percentage is — omitting the key leaves the old value in place.
      if (payload.status === 'Partially Paid') {
        const collected = parseFloat(form.amount_collected)
        payload.amount_collected = Number.isFinite(collected) && collected >= 0 ? collected : null
      } else {
        payload.amount_collected = null
      }
      const result = isNew
        ? await createDeal({ ...payload, created_by: currentUser?.id, org_id: currentUser?.org_id })
        : await updateDeal(deal.id, payload)
      if (result.error) throw new Error(result.error.message)
      onSaved()
    } catch (err) { setError(err.message) }
    setSaving(false)
  }

  const inp = {
    width: '100%', padding: '9px 11px', background: 'var(--bg3)',
    border: '1px solid var(--border)', borderRadius: 8,
    color: 'var(--text)', fontSize: 13, outline: 'none', boxSizing: 'border-box'
  }
  const lbl = {
    display: 'block', fontSize: 11, fontWeight: 600,
    color: 'var(--text2)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.5px'
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div {...dealDialog.props} className="fade-in" style={{
        background: 'var(--gradient-card)', border: '1px solid var(--card-border)',
        borderRadius: isMobile ? 12 : 16, padding: isMobile ? 16 : 28, width: '100%', maxWidth: isMobile ? 'calc(100vw - 24px)' : 600,
        maxHeight: '92vh', overflowY: 'auto', boxShadow: 'var(--shadow-xl)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700 }}>
            {isNew ? '+ New Deal' : 'Edit Deal'}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text2)', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>x</button>
        </div>

        {/* Contract auto-fill — admin only, and only on new deals so it can't
            quietly overwrite a deal someone already reconciled. */}
        {!deal && canParseContract(currentUser) && (
          <div style={{
            marginBottom: 16, padding: 14, borderRadius: 10,
            border: `1px dashed ${parseError ? 'rgba(239,68,68,0.4)' : 'var(--border)'}`,
            background: 'var(--bg3)',
          }}>
            {!parsedWarnings && !parseError && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <label style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '8px 16px', borderRadius: 8, border: 'none',
                  background: parsing ? 'var(--bg2)' : 'linear-gradient(135deg, #8b5cf6, #3b82f6)',
                  color: parsing ? 'var(--text3)' : '#fff', fontWeight: 700, fontSize: 13,
                  cursor: parsing ? 'wait' : 'pointer', fontFamily: 'var(--font-body)',
                }}>
                  {parsing ? 'Reading contract…' : '📄 Fill from contract'}
                  <input
                    type="file"
                    accept="application/pdf,image/*"
                    disabled={parsing}
                    onChange={handleContractUpload}
                    style={{ display: 'none' }}
                  />
                </label>
                <span style={{ fontSize: 12, color: 'var(--text3)' }}>
                  Upload a PDF or photo and the fields below fill in. You review before saving.
                </span>
              </div>
            )}

            {parseError && (
              <div style={{ fontSize: 13, color: '#ef4444', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                <span>{parseError}</span>
                <button onClick={() => setParseError(null)} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 16 }}>×</button>
              </div>
            )}

            {parsedWarnings && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: parsedWarnings.length ? 8 : 0 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#10b981' }}>
                    ✓ Filled from contract — check the amount and dates before saving
                  </span>
                  <button onClick={() => setParsedWarnings(null)} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 16 }}>×</button>
                </div>
                {parsedWarnings.map((w, i) => (
                  <div key={i} style={{ fontSize: 12, color: '#f59e0b', marginTop: 4 }}>⚠ {w}</div>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 10 : 14 }}>
          {[['date','Date','date'],['creator','Creator','text'],['client','Agency / Client','text'],['brand','Brand','text'],['contact_person','Contact Person','text'],['email','Email','email']].map(([k, l, t]) => (
            <div key={k}>
              <label style={lbl}>{l}</label>
              {acFields.has(k) ? (
                <AutocompleteInput
                  type={t}
                  value={form[k] || ''}
                  onChange={v => handleChange(k, v)}
                  suggestions={suggestions[k] || []}
                  style={inp}
                />
              ) : (
                <input type={t} value={form[k] || ''} onChange={e => handleChange(k, e.target.value)} style={inp} />
              )}
            </div>
          ))}

          <div style={{ gridColumn: '1/-1' }}>
            <label style={lbl}>Deliverables</label>
            <input value={form.deliverables || ''} onChange={e => handleChange('deliverables', e.target.value)} style={inp} placeholder="e.g. 1 Reel / 1 TikTok + Story Set" />
          </div>

          <div>
            <label style={lbl}>Deal Amount ($)</label>
            <input type="number" value={form.deal_amount || ''} onChange={e => handleChange('deal_amount', e.target.value)} style={inp} />
          </div>

          <div>
            <label style={lbl}>Agent</label>
            <select value={form.agent || ''} onChange={e => handleChange('agent', e.target.value)} style={inp}>
              {agentOptions.map(name => <option key={name} value={name}>{name}</option>)}
            </select>
          </div>

          <div>
            <label style={lbl}>Status</label>
            <select value={form.status || 'Concept'} onChange={e => handleChange('status', e.target.value)} style={inp}>
              {ALL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {form.status === 'Partially Paid' && (
            <div>
              <label style={lbl}>Collected So Far ($)</label>
              <input
                type="number" min="0" step="0.01"
                max={parseFloat(form.deal_amount) || undefined}
                value={form.amount_collected ?? ''}
                onChange={e => handleChange('amount_collected', e.target.value)}
                style={inp}
                placeholder={`of ${parseFloat(form.deal_amount) || 0}`}
              />
              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>
                {(() => {
                  const total = parseFloat(form.deal_amount) || 0
                  const got = parseFloat(form.amount_collected) || 0
                  if (got > total && total > 0) return 'More than the deal amount — this will not save.'
                  return `Outstanding: $${Math.max(total - got, 0).toLocaleString()}`
                })()}
              </div>
            </div>
          )}

          <div>
            <label style={lbl}>{DATE_ACTION_LABELS[form.status] || 'Date'}{!DATE_ACTION_LABELS[form.status] && <span style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 400, textTransform: 'none' }}> (N/A for this status)</span>}</label>
            <input
              type="date"
              value={form.date_action || ''}
              onChange={e => handleChange('date_action', e.target.value)}
              style={{ ...inp, opacity: DATE_ACTION_LABELS[form.status] ? 1 : 0.4, colorScheme: 'dark', cursor: 'pointer' }}
              disabled={!DATE_ACTION_LABELS[form.status]}
            />
          </div>

          <div>
            <label style={lbl}>Month</label>
            <select value={form.month || ''} onChange={e => handleChange('month', e.target.value)} style={inp}>
              {MONTHS_ORDER.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>

          {/* Commission breakdown */}
          <div style={{ gridColumn: '1/-1', background: 'var(--bg3)', borderRadius: 8, padding: 14, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
              Commission -- auto-calculated
            </div>
            {(() => {
              const ag = (form.agent || '').toLowerCase()
              const commItems = [['Total (20%)', 'commission_total']]
              if (ag === 'steve' || ag === 'mirelle' || ag === 'loizza' || ag === 'lydie' || ag === 'katrina' || !ag) commItems.push(['Steve', 'commission_steve'])
              if (ag === 'mirelle') commItems.push(['Mirelle', 'commission_mirelle'])
              if (ag === 'lydie') commItems.push(['Lydie', 'commission_lydie'])
              if (ag === 'loizza') commItems.push(['Loizza', 'commission_loizza'])
              const overrideItems = commItems.filter(([, k]) => k !== 'commission_total')
              return <>
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${commItems.length},1fr)`, gap: 8 }}>
                  {commItems.map(([l, k]) => (
                    <div key={k}>
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 3 }}>{l}</div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>${(form[k] || 0).toFixed(0)}</div>
                    </div>
                  ))}
                </div>
                {isAdmin && (
                  <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--border)', display: 'grid', gridTemplateColumns: `repeat(${overrideItems.length},1fr)`, gap: 8 }}>
                    <div style={{ fontSize: 10, color: 'var(--accent)', gridColumn: '1/-1', marginBottom: 4 }}>Override commissions manually</div>
                    {overrideItems.map(([l, k]) => (
                      <div key={k}>
                        <label style={{ ...lbl, fontSize: 10 }}>{l} $</label>
                        <input type="number" value={form[k] || 0} onChange={e => setForm(f => ({ ...f, [k]: parseFloat(e.target.value) || 0 }))} style={{ ...inp, fontSize: 12 }} />
                      </div>
                    ))}
                  </div>
                )}
              </>
            })()}
          </div>

          {/* Double Shots referral cut — admin only */}
          {isAdmin && (() => {
            const dsPct = parseFloat(form.double_shots_pct) || 0
            const dsCut = (parseFloat(form.deal_amount) || 0) * dsPct / 100
            return (
              <div style={{ gridColumn: '1/-1', background: 'var(--bg3)', borderRadius: 8, padding: 14, border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                  Double Shots (referral partner) — admin only
                </div>
                <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 10 }}>
                  Leave blank for a normal deal. Enter 15 when Double Shots sourced the deal, or 50 when you split commission with them. Tracked as an extra — does not change the agent cuts above.
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, alignItems: 'end' }}>
                  <div>
                    <label style={{ ...lbl, fontSize: 10 }}>Double Shots %</label>
                    <input type="number" min="0" max="100" value={form.double_shots_pct ?? ''} onChange={e => handleChange('double_shots_pct', e.target.value)} style={{ ...inp, fontSize: 12 }} placeholder="e.g. 15" />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 3 }}>Double Shots cut</div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: dsPct > 0 ? 'var(--accent)' : 'var(--text3)' }}>${dsCut.toFixed(0)}</div>
                  </div>
                </div>
              </div>
            )
          })()}

          {/* Document links */}
          <div style={{ gridColumn: '1/-1', background: 'var(--bg3)', borderRadius: 8, padding: 14, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
              Document Links
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 10 }}>
              <div>
                <label style={{ ...lbl, fontSize: 10 }}>Contract Link</label>
                <input value={form.contract_url || ''} onChange={e => handleChange('contract_url', e.target.value)} style={{ ...inp, fontSize: 12 }} placeholder="Google Drive link" />
              </div>
              <div>
                <label style={{ ...lbl, fontSize: 10 }}>Invoice Link</label>
                <input value={form.invoice_url || ''} onChange={e => handleChange('invoice_url', e.target.value)} style={{ ...inp, fontSize: 12 }} placeholder="Google Drive link" />
              </div>
              <div>
                <label style={{ ...lbl, fontSize: 10 }}>Content Link</label>
                <input value={form.content_url || ''} onChange={e => handleChange('content_url', e.target.value)} style={{ ...inp, fontSize: 12 }} placeholder="TikTok, IG or Drive link (several: separate with a space)" />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div style={{ gridColumn: '1/-1' }}>
            <label style={lbl}>Notes</label>
            <textarea value={form.notes || ''} onChange={e => handleChange('notes', e.target.value)} rows={3}
              style={{ ...inp, resize: 'vertical' }} placeholder="Context, next steps, special terms..." />
          </div>
        </div>

        {error && (
          <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '10px 12px', marginTop: 14, fontSize: 13, color: '#f87171' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 22, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text2)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Cancel
          </button>
          <button className="btn-hover" onClick={handleSave} disabled={saving} style={{
            padding: '9px 22px', borderRadius: 8, border: 'none',
            background: saving ? 'var(--border)' : 'linear-gradient(135deg, #f97316, #ef4444)',
            color: '#fff', fontSize: 13, fontWeight: 700, cursor: saving ? 'default' : 'pointer',
            boxShadow: saving ? 'none' : '0 4px 12px rgba(249,115,22,0.3)'
          }}>
            {saving ? 'Saving...' : isNew ? 'Create Deal' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  )
}
